import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Megaphone, FileImage, BookOpen, FileBarChart2, Image as ImgIcon,
  Globe, Send, Loader2, History, Mail,
} from "lucide-react";

interface KitEvent {
  id: string;
  region: string;
  title: string;
  status?: string;
  flyer_url?: string | null;
  flyer_approved?: boolean;
  conference_year?: number | null;
}

interface Props {
  event: KitEvent;
  userRole: string;
  isGlobalAdmin: boolean;
}

type ItemType = "flyer" | "magazine" | "report" | "photos" | "ribbon" | "conference_details";
type Audience = "public" | "kit_team" | "president_secretary" | "regional";

interface PublishItem {
  key: ItemType;
  title: string;
  description: string;
  audience: Audience;
  audienceLabel: string;
  icon: React.ReactNode;
  rolesAllowed: ("global_admin" | "president" | "regional_admin" | "kit_admin")[];
  needsApproval?: boolean;
  promptFields?: { name: string; label: string; type?: "text" | "textarea" | "email"; required?: boolean; placeholder?: string }[];
}

const ITEMS: PublishItem[] = [
  {
    key: "flyer",
    title: "Flyer to Public Site",
    description: "Push the approved flyer to the Conferences page and email regional contacts.",
    audience: "public",
    audienceLabel: "Public + Regional list",
    icon: <FileImage className="w-5 h-5" />,
    rolesAllowed: ["global_admin", "president", "regional_admin"],
    needsApproval: true,
    promptFields: [
      { name: "recipient_emails", label: "Email recipients (comma separated, optional)", type: "text", placeholder: "leader1@…, coord@…" },
      { name: "message", label: "Optional message", type: "textarea" },
    ],
  },
  {
    key: "magazine",
    title: "KIT Magazine Submission",
    description: "Send the magazine entry + photos to the KIT team.",
    audience: "kit_team",
    audienceLabel: "KIT Team",
    icon: <BookOpen className="w-5 h-5" />,
    rolesAllowed: ["global_admin", "president", "regional_admin", "kit_admin"],
    promptFields: [
      { name: "recipient_emails", label: "KIT team emails", type: "text", required: true, placeholder: "kit@ofuesi.org" },
      { name: "message", label: "Notes for the KIT team", type: "textarea" },
    ],
  },
  {
    key: "report",
    title: "Final Report",
    description: "Email a consolidated report (budget, attendance, outcomes) to President & Secretary.",
    audience: "president_secretary",
    audienceLabel: "President & Secretary",
    icon: <FileBarChart2 className="w-5 h-5" />,
    rolesAllowed: ["global_admin", "president", "regional_admin"],
    promptFields: [
      { name: "recipient_emails", label: "Recipient emails", type: "text", required: true, placeholder: "president@…, secretary@…" },
      { name: "summary", label: "Executive summary", type: "textarea", required: true },
    ],
  },
  {
    key: "photos",
    title: "Photos to Public Gallery",
    description: "Submit conference photos to the public Gallery (global admin approves).",
    audience: "public",
    audienceLabel: "Public Gallery",
    icon: <ImgIcon className="w-5 h-5" />,
    rolesAllowed: ["global_admin", "president", "regional_admin"],
  },
  {
    key: "ribbon",
    title: "Homepage Announcement Ribbon",
    description: "Show a homepage ribbon announcing this conference.",
    audience: "public",
    audienceLabel: "Public Homepage",
    icon: <Megaphone className="w-5 h-5" />,
    rolesAllowed: ["global_admin", "president"],
    promptFields: [
      { name: "ribbon_text", label: "Ribbon text", type: "text", required: true, placeholder: "Register now for the 2026 Regional Conference!" },
      { name: "ribbon_link", label: "Ribbon link (optional)", type: "text" },
    ],
  },
  {
    key: "conference_details",
    title: "Conference Details to Public Site",
    description: "Sync schedule, speakers, and registration link to the public Conferences page.",
    audience: "public",
    audienceLabel: "Public Site",
    icon: <Globe className="w-5 h-5" />,
    rolesAllowed: ["global_admin", "president", "regional_admin"],
  },
];

export const PublishWorkspace: React.FC<Props> = ({ event, userRole, isGlobalAdmin }) => {
  const [log, setLog] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<PublishItem | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const loadLog = async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("conference_kit_publish_log")
      .select("*")
      .eq("kit_event_id", event.id)
      .order("published_at", { ascending: false })
      .limit(50);
    setLog(data || []);
    setLoading(false);
  };

  useEffect(() => { loadLog(); }, [event.id]);

  const can = (item: PublishItem) =>
    isGlobalAdmin || item.rolesAllowed.includes(userRole as any);

  const lastPublished = (k: ItemType) =>
    log.find((l) => l.item_type === k);

  const openPublish = (item: PublishItem) => {
    if (item.needsApproval && !event.flyer_approved && item.key === "flyer") {
      toast({
        title: "Flyer not approved yet",
        description: "Get the Regional Coordinator's approval in the Flyer Builder first.",
        variant: "destructive",
      });
      return;
    }
    setActive(item);
    setFormData({});
  };

  const submit = async () => {
    if (!active) return;
    // Validate required fields
    const missing = (active.promptFields || []).filter((f) => f.required && !formData[f.name]?.trim());
    if (missing.length) {
      toast({ title: "Missing fields", description: missing.map((m) => m.label).join(", "), variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("kit-publish", {
        body: {
          kit_event_id: event.id,
          item_type: active.key,
          audience: active.audience,
          payload: formData,
        },
      });
      if (error) throw error;
      toast({ title: "Published", description: (data as any)?.message || `${active.title} sent.` });
      setActive(null);
      loadLog();
    } catch (e: any) {
      toast({ title: "Publish failed", description: e.message, variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {ITEMS.map((item) => {
          const allowed = can(item);
          const last = lastPublished(item.key);
          return (
            <Card key={item.key} className={!allowed ? "opacity-60" : ""}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="text-primary">{item.icon}</span>
                  {item.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground min-h-[40px]">{item.description}</p>
                <div className="flex items-center justify-between text-xs">
                  <Badge variant="outline" className="text-[10px]">
                    <Send className="w-3 h-3 mr-1" /> {item.audienceLabel}
                  </Badge>
                  {last && (
                    <span className="text-muted-foreground" title={last.published_at}>
                      Last: {new Date(last.published_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <Button
                  className="w-full"
                  size="sm"
                  disabled={!allowed}
                  onClick={() => openPublish(item)}
                >
                  {last ? "Publish Again" : "Publish"}
                </Button>
                {!allowed && (
                  <p className="text-[10px] text-muted-foreground">Requires higher role.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" /> Publish History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="animate-spin" />
          ) : log.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nothing published yet.</p>
          ) : (
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {log.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs">{new Date(l.published_at).toLocaleString()}</TableCell>
                      <TableCell className="text-xs capitalize">{String(l.item_type).replace("_", " ")}</TableCell>
                      <TableCell className="text-xs capitalize">{String(l.audience).replace("_", " ")}</TableCell>
                      <TableCell className="text-xs">{l.published_by_name || "—"}</TableCell>
                      <TableCell>
                        <Badge variant={l.status === "success" ? "default" : "destructive"}>{l.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {active?.icon} Publish: {active?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{active?.description}</p>
            {(active?.promptFields || []).map((f) => (
              <div key={f.name} className="space-y-1">
                <label className="text-xs font-medium">
                  {f.label} {f.required && <span className="text-red-600">*</span>}
                </label>
                {f.type === "textarea" ? (
                  <Textarea
                    rows={3}
                    placeholder={f.placeholder}
                    value={formData[f.name] || ""}
                    onChange={(e) => setFormData((d) => ({ ...d, [f.name]: e.target.value }))}
                  />
                ) : (
                  <Input
                    type={f.type === "email" ? "email" : "text"}
                    placeholder={f.placeholder}
                    value={formData[f.name] || ""}
                    onChange={(e) => setFormData((d) => ({ ...d, [f.name]: e.target.value }))}
                  />
                )}
              </div>
            ))}
            {!active?.promptFields?.length && (
              <p className="text-xs text-muted-foreground">No additional input needed. Click Publish to confirm.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
